"use client"

import {usePathname} from "next/navigation"
import {useActionState} from "react"

import type {DemoSwitchState} from "@/app/(auth)/login/actions"
import {switchDemoAccountAction} from "@/app/(auth)/login/actions"
import {Button, Callout, NativeSelect} from "@/components/ui"
import type {DemoAccount} from "@/lib/demo"

/**
 * The demo role switcher (lib/demo.ts). `panel` lists every account on the
 * sign-in page; `compact` is the sidebar's "View as" picker, which signs in as
 * the chosen account and stays on the current page.
 *
 * Renders nothing when the feature is off (no accounts configured).
 */
export const DemoSwitcher = ({
  accounts,
  current,
  variant,
  returnUri,
}: {
  accounts: readonly DemoAccount[]
  /** The signed-in email, to mark which role is showing. */
  current?: string | undefined
  variant: "panel" | "compact"
  /** Where to land after switching; the current page when omitted. */
  returnUri?: string
}) => {
  const [state, action, pending] = useActionState<DemoSwitchState, FormData>(
    switchDemoAccountAction,
    {ok: true},
  )
  const pathname = usePathname()
  if (accounts.length === 0) return null
  const target = returnUri ?? pathname

  const error = state.ok === false && state.message !== undefined && (
    <Callout tone="danger" title="Could not switch">
      <p>{state.message}</p>
    </Callout>
  )

  if (variant === "compact") {
    return (
      <form action={action} className="flex flex-col gap-1.5">
        <label
          htmlFor="demo-view-as"
          className="font-sans text-[11px] font-bold uppercase tracking-[0.08em] text-muted-foreground"
        >
          Demo: view as
        </label>
        <input type="hidden" name="returnUri" value={target} />
        <div className="flex items-center gap-2">
          <NativeSelect
            id="demo-view-as"
            name="email"
            defaultValue={current}
            className="min-w-0 flex-1"
          >
            {accounts.map(account => (
              <option key={account.email} value={account.email}>
                {account.label}
              </option>
            ))}
          </NativeSelect>
          <Button type="submit" variant="secondary" size="mini" disabled={pending}>
            {pending ? "…" : "Go"}
          </Button>
        </div>
        {error}
      </form>
    )
  }

  return (
    <div className="flex flex-col gap-3">
      <div>
        <h2 className="font-display text-sm font-bold uppercase tracking-wide">Demo accounts</h2>
        <p className="text-xs text-muted-foreground">
          Sign in as each kind of user to see the portal as they do.
        </p>
      </div>
      <ul className="flex flex-col gap-2">
        {accounts.map(account => (
          <li key={account.email}>
            <form action={action}>
              <input type="hidden" name="email" value={account.email} />
              <input type="hidden" name="returnUri" value={target} />
              <button
                type="submit"
                disabled={pending}
                className="flex w-full flex-col items-start rounded-list border border-card-border bg-card px-3 py-2 text-left transition-colors hover:border-ring focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-60"
              >
                <span className="text-sm font-semibold">{account.label}</span>
                <span className="text-xs text-muted-foreground">
                  {account.about === "" ? account.email : `${account.about} · ${account.email}`}
                </span>
              </button>
            </form>
          </li>
        ))}
      </ul>
      {error}
    </div>
  )
}
